import { Text, Badge, HStack, VStack, Box, Avatar } from "@chakra-ui/react";
import { Student } from "../../../services/Dtos";
import { genderMap } from "../../../services/constants";
import Empty from "../../CustomUi/Empty";
import { useNavigate } from "react-router-dom";
import useThemeColors from "../../../hooks/useThemeColors";

interface Props {
  students: Student[] | null;
  chosenStudent: Student | null;
  handleRowClick: (student: Student) => void;
  selectedClassId: string;
}

export default function ResultStudentsTable({
  students,
  chosenStudent,
  handleRowClick,
  selectedClassId: selectedClass,
}: Props) {
  const colors = useThemeColors();
  const navigate = useNavigate();

  if (!selectedClass) {
    return <Empty message="No class selected" />;
  }

  if (!students?.length) {
    return (
      <Empty
        hidePicture
        message="Start by adding students to this class"
        hasButton
        buttonText="Go to Students"
        handleClick={() => navigate("/students")}
      />
    );
  }

  return (
    <VStack gap={2} align="stretch">
      {students &&
        students.map((s) => (
          <Box
            key={s.id}
            onClick={() => handleRowClick(s)}
            cursor="pointer"
            transition="all 0.2s ease"
            position="relative"
            _hover={{
              bg: colors.background.subtle,
              transform: "translateY(-1px)",
            }}
            _active={{
              transform: "scale(0.98)",
            }}
            _before={
              chosenStudent?.id === s.id
                ? {
                    content: '""',
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: "3px",
                    bg: colors.schoolAccent.icon,
                  }
                : {}
            }
            bg={
              chosenStudent?.id === s.id
                ? colors.background.tertiary
                : "transparent"
            }
            borderRadius="md"
            p={3}
          >
            <HStack gap={3} align="center">
              <Avatar.Root size="sm">
                <Avatar.Image src={s.avatarUrl} />
                <Avatar.Fallback
                  name={`${s.firstName} ${s.lastName}`}
                  bg={colors.background.secondary}
                >
                  {`${s.firstName.charAt(0)} ${s.lastName.charAt(0)}`}
                </Avatar.Fallback>
              </Avatar.Root>
              <VStack gap={1} align="start" flex={1}>
                <Text fontWeight="medium" fontSize="sm">
                  {`${s.firstName} ${s.middleName} ${s.lastName}`}
                </Text>
                <HStack gap={2}>
                  <Text
                    fontSize="xs"
                    color={colors.text.secondary}
                    fontFamily="mono"
                  >
                    {s.publicId}
                  </Text>
                  <Badge
                    size="sm"
                    variant="subtle"
                    colorPalette="gray"
                    fontSize="xs"
                  >
                    {genderMap[s.gender]}
                  </Badge>
                </HStack>
              </VStack>
            </HStack>
          </Box>
        ))}
    </VStack>
  );
}
